import React, { useState, useEffect, useCallback, useMemo } from 'react'

import {
  Card,
  Icon,
  Like,
  Dislike,
  Chat,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  number, string, arrayOf, shape, oneOf, bool,
} from 'prop-types'

import Rating from 'Pods/LazyLoaded/ReviewsSidebar/Components/Rating/Rating'
import { FILTER_TYPES } from 'Pods/LazyLoaded/ReviewsSidebar/constants'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import ReviewsAreaButton from './Components/ReviewsAreaButton/ReviewsAreaButton'
import useItemReviews from './hooks/useItemReviews'
import { sortItemsByPriority } from '../../../ReviewsArea/utils/sortItems'
import AddButton from '../../../Shared/AddButton/AddButton'
import AreaHeader from '../../../Shared/AreaHeader/AreaHeader'

let Sidebar

const iconSize = { height: '40px', width: '40px' }

function ReviewsArea({
  commentsCount = 0,
  comments: initialComments,
  dislikeCount = 0,
  hasMoreComments,
  hasMoreRatings,
  likeCount = 0,
  ratings: initialRatings = EMPTY_ARRAY,
  role,
  canReview,
  userName,
  isCurrentUserAdmin = false,
  isLoggedIn = false,
  productId,
  productOwnerUsername,
}) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [initialFilterBy, setInitialFilterBy] = useState('')
  const [isSidebarLoaded, setIsSidebarLoaded] = useState(false)

  const {
    comments,
    ratings,
    counts,
    addReviewOptimistic,
    removeReviewOptimistic,
  } = useItemReviews(
    initialComments,
    initialRatings,
    commentsCount,
    likeCount,
    dislikeCount,
    userName
  )

  useEffect(() => {
    if (isSidebarOpen && !Sidebar && !isSidebarLoaded) {
      importSidebar()
    }
  }, [isSidebarOpen, isSidebarLoaded])

  function importSidebar() {
    import('Pods/LazyLoaded/ReviewsSidebar/ReviewsSidebar')
      .then((module) => {
        Sidebar = module.default
        setIsSidebarLoaded(true)
      })
      .catch((error) => {
        console.error('Failed to load ReviewsSidebar:', error)
      })
  }

  function handleMouseEnter() {
    if (!isSidebarLoaded && !Sidebar) {
      importSidebar()
    }
  }

  function handleTouchStart() {
    if (!isSidebarLoaded && !Sidebar) {
      importSidebar()
    }
  }

  const handleOpenSidebar = useCallback((event) => {
    const filterBy = event?.detail?.filterBy || FILTER_TYPES.POSITIVE

    setInitialFilterBy(filterBy)
    setIsSidebarOpen(true)
  }, [])

  useEffect(() => {
    window.addEventListener('open-review-modal', handleOpenSidebar)

    return () => {
      window.removeEventListener('open-review-modal', handleOpenSidebar)
    }
  }, [])

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false)
  }, [])

  const handleLikeClick = useCallback(() => {
    handleOpenSidebar({ detail: { filterBy: FILTER_TYPES.POSITIVE } })
  }, [handleOpenSidebar])

  const handleDislikeClick = useCallback(() => {
    handleOpenSidebar({ detail: { filterBy: FILTER_TYPES.NEGATIVE } })
  }, [handleOpenSidebar])

  const handleCommentClick = useCallback(() => {
    handleOpenSidebar({ detail: { filterBy: FILTER_TYPES.COMMENTS } })
  }, [handleOpenSidebar])

  const hasMoreThanOneItem = (counts.comments + counts.positive + counts.negative) > 1

  const displayedItems = useMemo(() => {
    const commentsWithType = comments.map(comment => ({ ...comment, itemType: 'comment' }))
    const ratingsWithType = ratings.map(rating => ({ ...rating, itemType: 'rating' }))

    // Combine all items and sort by priority
    const allItems = [...ratingsWithType, ...commentsWithType]
    const sortedItems = sortItemsByPriority(allItems)

    return sortedItems.slice(0, 6)
  }, [comments, ratings])
  const hasNoItems = displayedItems.length === 0
  const hasOneSingleItem = displayedItems.length === 1
  const tooltipContent = (
    <span>
      See what other buyers think about this model - real feedback on quality,<br/>
      accuracy, and usability.
    </span>
  )

  return (
    <>
      <Card
        hasBorder
        isRounded
        customClass="reviews-area"
      >
        <AreaHeader
          title="REVIEWS & COMMENTS"
          tooltipContent={tooltipContent}
          tooltipId="review-and-comments"
          actions={
            <>
              {hasMoreThanOneItem && (
                <>
                  <ReviewsAreaButton
                    onClick={handleOpenSidebar}
                    onMouseEnter={handleMouseEnter}
                    onTouchStart={handleTouchStart}
                    text="View all"
                    className="area-action-button--mobile-only"
                  />
                  <ReviewsAreaButton
                    icon={Like}
                    iconColor="light"
                    backgroundColor="green"
                    onClick={handleLikeClick}
                    onMouseEnter={handleMouseEnter}
                    onTouchStart={handleTouchStart}
                    text={counts.positive.toString()}
                    className="area-action-button--desktop-only"
                  />
                  <ReviewsAreaButton
                    icon={Dislike}
                    iconColor="light"
                    backgroundColor="orange"
                    onClick={handleDislikeClick}
                    onMouseEnter={handleMouseEnter}
                    onTouchStart={handleTouchStart}
                    text={counts.negative.toString()}
                    className="area-action-button--desktop-only"
                  />
                  <ReviewsAreaButton
                    icon={Chat}
                    iconColor="dark"
                    backgroundColor="gray"
                    onClick={handleCommentClick}
                    onMouseEnter={handleMouseEnter}
                    onTouchStart={handleTouchStart}
                    text={counts.comments.toString()}
                    className="area-action-button--desktop-only"
                  />
                </>
              )}
              <AddButton
                id="add-comment-button"
                openSidebar={handleOpenSidebar}
                onMouseEnter={handleMouseEnter}
                onTouchStart={handleTouchStart}
                ariaLabel="Add review or comment"
              />
            </>
          }
        />
        <div
          className={classnames(
            'reviews-area__body',
            hasOneSingleItem && 'reviews-area__body--single',
            hasNoItems && 'reviews-area__body--empty',
          )}
        >
          {displayedItems.map(({
            id,
            username: usernameRating,
            content,
            updatedAt,
            score,
            comment,
            itemType,
            avatar,
            anonymous,
            userRole,
            replies,
          }) => (
            <Rating
              key={`${itemType}_${id}`}
              isCurrentUserAdmin={isCurrentUserAdmin}
              id={id}
              userName={usernameRating}
              content={content || comment}
              updatedAt={updatedAt}
              rating={score}
              avatar={avatar}
              anonymous={anonymous}
              userRole={userRole}
              isLoggedIn={isLoggedIn}
              ariaLabelPrefix={`${usernameRating} rating`}
              replies={replies || []}
              productId={productId}
            />
          ))}
          {hasNoItems && (
            <div className="reviews-area__body-empty-container">
              <Icon svgComponent={Chat} size={iconSize} />
              <span className="reviews-area__body-empty-text">
                There are no reviews or comments yet. Please be the first one to write it.
              </span>
            </div>
          )}
        </div>
      </Card>
      {isSidebarOpen && isSidebarLoaded && Sidebar && (
        <Sidebar
          isOpen={isSidebarOpen}
          closeSidebar={handleCloseSidebar}
          likeCount={counts.positive}
          dislikeCount={counts.negative}
          commentsCount={counts.comments}
          comments={comments}
          ratings={ratings}
          initialFilterBy={initialFilterBy}
          onReviewAdd={addReviewOptimistic}
          onReviewRemove={removeReviewOptimistic}
          userName={userName}
          role={role}
          canReview={canReview}
          hasMoreComments={hasMoreComments}
          hasMoreRatings={hasMoreRatings}
          isLoggedIn={isLoggedIn}
          isCurrentUserAdmin={isCurrentUserAdmin}
          productId={productId}
          productOwnerUsername={productOwnerUsername}
        />
      )}
    </>
  )
}

ReviewsArea.propTypes = {
  isCurrentUserAdmin: bool,
  commentsCount: number,
  comments: arrayOf(shape({
    id: number,
    userName: string,
    content: string,
    updatedAt: string,
    userRole: oneOf(['seller', 'multiple_buyer', 'buyer', 'community_member']),
  })).isRequired,
  hasMoreComments: bool.isRequired,
  hasMoreRatings: bool.isRequired,
  likeCount: number,
  dislikeCount: number,
  productId: number.isRequired,
  productOwnerUsername: string.isRequired,
  ratings: arrayOf(shape({
    anonymous: bool,
    authorId: number,
    automatedReview: bool,
    avatar: shape({
      small: shape({
        url: string,
      }),
      url: string,
    }),
    comment: string,
    createdAt: string,
    id: number,
    itemId: number,
    score: number,
    updatedAt: string,
    userId: number,
    username: string,
    userRole: oneOf(['seller', 'multiple_buyer', 'buyer', 'community_member']),
  })),
  role: oneOf(['guest', 'user', 'buyer', 'seller']).isRequired,
  canReview: bool,
  userName: string,
  isLoggedIn: bool,
}

export default ReviewsArea
